import React, { type ReactNode, type CSSProperties, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';

import { ReactRenderer } from '@atlaskit/renderer';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMediaFeatureFlags } from '@confluence/fabric-media-support/entry-points/mediaFeatureFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ANALYTICS_OPERATIONAL_SUBJECT, ANALYTICS_ORIGIN } from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/useSnippetAnalytics.ts';
import type { Snippet } from '../snippetTypes.ts';
import { wrapAdfWithDocNode } from '../snippetUtils.ts';

import { getProviders } from './providers.ts';

const styles = cssMap({
	scaledContainer: {
		overflow: 'hidden',
		userSelect: 'none',
		pointerEvents: 'none',
	},
	scaledContent: {
		height: 'auto',
		transformOrigin: 'top left',
	},
	loadingWrapper: {
		width: '100%',
		height: '100%',
	},
});

/** Desired output dimensions (container size) */
export type Dimensions = Pick<CSSProperties, 'width' | 'maxHeight'>;

export const SnippetPreview = ({
	snippet,
	dimensions,
	/** Width in px at which content renders before scaling */
	renderWidth,
	loadingComponent,
}: {
	snippet: Snippet;
	dimensions: Dimensions;
	renderWidth: number;
	loadingComponent?: ReactNode;
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const showFallback = !!loadingComponent && isLoading;

	return (
		<>
			{showFallback && (
				<Flex xcss={styles.loadingWrapper} alignItems="center" justifyContent="center">
					{loadingComponent}
				</Flex>
			)}
			<ScaledContainer dimensions={dimensions} renderWidth={renderWidth} shouldHide={showFallback}>
				<Renderer
					adf={snippet.body}
					snippetId={snippet.snippetId}
					onRendered={() => setIsLoading(false)}
				/>
			</ScaledContainer>
		</>
	);
};

const Renderer = ({
	adf,
	snippetId,
	onRendered,
}: {
	adf: any;
	snippetId: string;
	onRendered: () => void;
}) => {
	const { cloudId, userId, featureFlags } = useSessionData();
	const { fireFailedAnalytic } = useSnippetAnalytics(ANALYTICS_ORIGIN.SNIPPET_PANEL);

	const handleRenderError = (error: any) => {
		const reasonString = error instanceof Error ? error.message : JSON.stringify(error);
		const errorToLog = new Error(`Error when rendering snippet preview: ${reasonString}`);
		getMonitoringClient().submitError(errorToLog, {
			attribution: Attribution.INTEGRATIONS,
		});
		fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_PREVIEW_RENDER, {});
	};

	const providers = getProviders(cloudId, userId, snippetId);

	return (
		<ReactRenderer
			adfStage="stage0"
			document={wrapAdfWithDocNode(adf)}
			appearance="full-page"
			allowColumnSorting={false}
			enableSsrInlineScripts={false}
			allowHeadingAnchorLinks={false}
			stickyHeaders={undefined}
			dataProviders={providers}
			onComplete={onRendered}
			onError={handleRenderError}
			allowPlaceholderText
			media={{
				featureFlags: getMediaFeatureFlags(featureFlags),
			}}
		/>
	);
};

const ScaledContainer = ({
	children,
	dimensions,
	renderWidth,
	shouldHide = false,
}: {
	children: React.ReactNode;
	dimensions: Dimensions;
	renderWidth: number;
	shouldHide?: boolean;
}) => {
	return (
		<Box
			ref={(node: HTMLDivElement | null) => node?.setAttribute('inert', '')}
			xcss={styles.scaledContainer}
			testId="snippet-preview-container"
			style={{
				width: dimensions.width,
				maxHeight: dimensions.maxHeight,
				display: shouldHide ? 'none' : 'block',
			}}
		>
			<Box
				xcss={styles.scaledContent}
				style={{
					transform: `scale(calc((${dimensions.width}) / ${renderWidth}px))`,
					width: renderWidth,
				}}
			>
				{children}
			</Box>
		</Box>
	);
};
